export type SpectraArraysSource = {
  wavelengths?: number[];
  values?: number[];
};

export function hasSpectraArrays(
  source?: SpectraArraysSource | null
): source is SpectraArraysSource & { wavelengths: number[] } {
  return Boolean(source?.wavelengths?.length);
}

/** Unique, sorted wavelengths from any number of spectra (mixed device grids). */
export function uniqueSortedWavelengths(
  items: (SpectraArraysSource | null | undefined)[]
): number[] {
  const wavelengths = new Set<number>();
  items.forEach((item) => {
    item?.wavelengths?.forEach((wavelength) => {
      if (Number.isFinite(wavelength)) wavelengths.add(wavelength);
    });
  });
  return Array.from(wavelengths).sort((a, b) => a - b);
}

export function getSpectraDataId(
  spectraData?: string | { _id?: string } | null
): string {
  if (!spectraData) return "";
  if (typeof spectraData === "string") return spectraData;
  return spectraData._id || "";
}

export function getSpectraValueAtWavelength(
  source?: SpectraArraysSource | null,
  wavelength?: number
): number | undefined {
  if (!source?.wavelengths?.length || wavelength == null) return undefined;
  const index = source.wavelengths.findIndex((w) => w == wavelength);
  if (index < 0) return undefined;
  const value = source.values?.[index];
  return Number.isFinite(value) ? value : undefined;
}

/** Linear interpolation when the exact wavelength is missing (e.g. other device grid). */
export function getSpectraValueAtWavelengthInterpolated(
  source?: SpectraArraysSource | null,
  wavelength?: number
): number | undefined {
  const exact = getSpectraValueAtWavelength(source, wavelength);
  if (exact !== undefined) return exact;
  if (!source?.wavelengths?.length || wavelength == null || !source.values?.length) {
    return undefined;
  }
  const points: { w: number; v: number }[] = [];
  for (let i = 0; i < source.wavelengths.length; i++) {
    const w = source.wavelengths[i];
    const v = source.values[i];
    if (!Number.isFinite(w) || !Number.isFinite(v)) continue;
    points.push({ w, v });
  }
  if (points.length < 2) return undefined;
  points.sort((a, b) => a.w - b.w);
  if (wavelength < points[0].w || wavelength > points[points.length - 1].w) {
    return undefined;
  }
  for (let i = 0; i < points.length - 1; i++) {
    const left = points[i];
    const right = points[i + 1];
    if (wavelength >= left.w && wavelength <= right.w) {
      if (right.w === left.w) return left.v;
      return (
        left.v + ((wavelength - left.w) / (right.w - left.w)) * (right.v - left.v)
      );
    }
  }
  return undefined;
}

export type SpectraEnvelope = {
  mean: (number | null)[];
  low: (number | null)[];
  high: (number | null)[];
  sampleCount: number;
};

/** Mean ± 1 sample SD of absorbance at each wavelength. Needs ≥2 values per point. */
export function computeSpectraEnvelope(
  sources: SpectraArraysSource[],
  wavelengths: number[]
): SpectraEnvelope {
  const usable = sources.filter(hasSpectraArrays);
  const mean: (number | null)[] = [];
  const low: (number | null)[] = [];
  const high: (number | null)[] = [];
  for (const wavelength of wavelengths) {
    const values = usable
      .map((source) =>
        getSpectraValueAtWavelengthInterpolated(source, wavelength)
      )
      .filter((value): value is number => value !== undefined);
    if (values.length < 2) {
      mean.push(null);
      low.push(null);
      high.push(null);
      continue;
    }
    const average = values.reduce((sum, value) => sum + value, 0) / values.length;
    const variance =
      values.reduce((sum, value) => sum + (value - average) ** 2, 0) /
      (values.length - 1);
    const stdDev = Math.sqrt(variance);
    mean.push(average);
    low.push(average - stdDev);
    high.push(average + stdDev);
  }
  return { mean, low, high, sampleCount: usable.length };
}

export function envelopeHasBand(envelope?: SpectraEnvelope | null): boolean {
  return Boolean(envelope?.low.some((value) => value != null));
}
